import { Skeleton } from 'antd';
import { useAppSelector } from 'lib/store/rootReducer';
import { Redirect, RedirectProps, Route, RouteProps } from 'react-router-dom';
import { resolveRedirectPath } from './private-route';

export interface PublicRouteProps
  extends Omit<RouteProps, 'render' | 'children'> {
  /**
   * If `publicOnly` is set to true then only non-authenticated users can see the route.
   * All other users will be redirected
   */
  publicOnly?: boolean;

  /**
   * The path to redirect the user to if they do not meet the route conditions.
   *
   * This is only applied if `publicOnly` is set, and the redirect condition is triggered.
   */
  redirectPath: RedirectProps['to'];
  redirectShouldPreserveSearchQuery?: boolean;
}

export function PublicRoute(props: PublicRouteProps) {
  const {
    publicOnly,
    redirectPath,
    redirectShouldPreserveSearchQuery,
    component: Component,
    ...rest
  } = props;
  const user = useAppSelector(state => state.auth.user);
  const isLoading = useAppSelector(state => state.auth.isUserDataLoading);

  if (!Component) return <>Nothing here</>;
  if (!user && isLoading) return <Skeleton active />;

  return (
    <Route
      {...rest}
      render={args => {
        if (publicOnly && user) {
          /**
           * publicOnly = true
           *
           * Redirect the user because they are signed in
           */
          return (
            <Redirect
              to={{
                search: redirectShouldPreserveSearchQuery
                  ? location.search
                  : '',
                ...resolveRedirectPath(redirectPath)
              }}
              push
            />
          );
        }

        /**
         * Render the component if this component is available for everyone
         * Otherwise only render for non-authenticated users
         */
        return <Component {...args} />;
      }}
    />
  );
}
